// Libs
import clsx from "clsx";
import moment from "moment";
import React from "react";
import { useTranslation } from "src/design-system/core/TranslationContext";

// Utils
import {
    FILTERS_COMPONENTS_TRANSLATION_NAMESPACE,
    TIMESLOT_FILTER_TK,
} from "src/components/Filters/config";
import { formatDurationToTime, formatTimeToDuration } from "src/utils/formatters";
import { useTimeSlotSelectFilterStyles } from "./TimeSlotSelectFilter.styles";
import { formatTimeSlotFilter } from "./hooks";

// Components
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    InputAdornment,
    Popover,
    Radio,
    RadioGroup,
    TextField,
} from "@mui/material";
import { TimePicker } from "src/components/Formik/TimePicker/TimePicker";

enum TimeSlotSelectFilterChoice {
    ALL_DAY = "allday",
    CUSTOM = "custom",
}

interface TimeSlotSelectFilterValue {
    allday?: boolean;
    durationStartFromMidnight?: moment.Duration;
    durationEndFromMidnight?: moment.Duration;
}

interface TimeSlotSelectFilterProps {
    headerFilter: TimeSlotSelectFilterValue;
    multipleHandleFilter: (value: TimeSlotSelectFilterValue) => void;
    onClose?: () => void;
}

const TIMEPICKER_MINUTES = [0, 30];
const TIMEPICKER_DEFAULT_OPEN_VALUE = moment.duration("12:00:00");
const TIMEPICKER_MIN_TIME = "05:00";
const TIMEPICKER_MAX_TIME = "29:00";

function getDefaultEndAt(startDuration: moment.Duration) {
    const endDuration = moment.duration(startDuration).add(2, "hours");

    return endDuration;
}

export function TimeSlotSelectFilter({
    headerFilter,
    multipleHandleFilter,
    onClose,
}: TimeSlotSelectFilterProps): JSX.Element {
    const classes = useTimeSlotSelectFilterStyles();
    const { t: translateRaw } = useTranslation();
    const { t, translateCommon } = useTranslation(
        `${FILTERS_COMPONENTS_TRANSLATION_NAMESPACE}:${TIMESLOT_FILTER_TK}`
    );

    const allday = headerFilter.allday === undefined ? true : headerFilter.allday;
    const durationStartFromMidnight = headerFilter.durationStartFromMidnight;
    const durationEndFromMidnight = headerFilter.durationEndFromMidnight;

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleAllDayChange = () => {
        multipleHandleFilter({
            allday: true,
        });
    };

    const handleCustomChange = () => {
        multipleHandleFilter({
            allday: false,
        });
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);

        if (onClose) {
            onClose();
        }
    };

    const handleChoiceToggle = () => {
        if (allday) {
            handleCustomChange();
        } else {
            handleAllDayChange();
        }
    };

    const handleTimeChange = (
        field: "durationStartFromMidnight" | "durationEndFromMidnight",
        val: moment.Duration
    ) => {
        const start = field === "durationStartFromMidnight" ? val : durationStartFromMidnight;
        const end = field === "durationEndFromMidnight" ? val : durationEndFromMidnight;

        if (field === "durationStartFromMidnight") {
            if (start && (!end || start > end)) {
                multipleHandleFilter({
                    durationStartFromMidnight: start,
                    durationEndFromMidnight: getDefaultEndAt(start),
                });
            } else {
                multipleHandleFilter({
                    durationStartFromMidnight: start,
                });
            }
        } else {
            multipleHandleFilter({
                durationEndFromMidnight: end,
            });
        }
    };

    const handleTimeFocus = () => {
        if (allday) {
            handleChoiceToggle();
        }
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;
    return (
        <React.Fragment>
            <Button
                className={classes.button}
                disableRipple
                disableFocusRipple
                aria-describedby={id}
                onClick={handleClick}
            >
                <TextField
                    slotProps={{
                        htmlInput: { className: classes.input },
                        input: {
                            readOnly: true,
                            disableUnderline: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <ArrowDropDown style={{ opacity: 0.7 }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    value={formatTimeSlotFilter(
                        {
                            durationStartFromMidnight: durationStartFromMidnight,
                            durationEndFromMidnight: durationEndFromMidnight,
                        },
                        translateRaw
                    )}
                />
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <Box className={classes.radioGroupContainer}>
                    <FormControl size="medium">
                        <RadioGroup
                            name="allDay"
                            value={
                                allday
                                    ? TimeSlotSelectFilterChoice.ALL_DAY
                                    : TimeSlotSelectFilterChoice.CUSTOM
                            }
                            onChange={handleChoiceToggle}
                        >
                            <FormControlLabel
                                value={TimeSlotSelectFilterChoice.ALL_DAY}
                                control={<Radio size="small" />}
                                label={t("allDay")}
                                classes={{ label: classes.textSize }}
                            />
                            <FormControlLabel
                                value={TimeSlotSelectFilterChoice.CUSTOM}
                                control={<Radio size="small" />}
                                label={t("custom")}
                                classes={{ label: classes.textSize }}
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>
                <Box
                    onFocus={handleTimeFocus}
                    className={clsx(classes.timePickersContainer, {
                        [classes.disabledStyle]: allday,
                    })}
                >
                    <TimePicker
                        className={classes.timePicker}
                        value={formatDurationToTime(durationStartFromMidnight, translateCommon)}
                        defaultOpenValue={formatDurationToTime(
                            TIMEPICKER_DEFAULT_OPEN_VALUE,
                            translateCommon
                        )}
                        minTime={TIMEPICKER_MIN_TIME}
                        maxTime={TIMEPICKER_MAX_TIME}
                        minutes={TIMEPICKER_MINUTES}
                        helperText={t("startAtHelper")}
                        onChange={(val) =>
                            handleTimeChange(
                                "durationStartFromMidnight",
                                formatTimeToDuration(val, translateCommon)
                            )
                        }
                    />

                    <div className={classes.timePickersSpacer}>
                        <span>{t("to")}</span>
                    </div>

                    <TimePicker
                        className={classes.timePicker}
                        value={formatDurationToTime(durationEndFromMidnight, translateCommon)}
                        minTime={formatDurationToTime(durationStartFromMidnight, translateCommon)}
                        minutes={TIMEPICKER_MINUTES}
                        maxTime={TIMEPICKER_MAX_TIME}
                        helperText={t("endAtHelper")}
                        onChange={(val) =>
                            handleTimeChange(
                                "durationEndFromMidnight",
                                formatTimeToDuration(val, translateCommon)
                            )
                        }
                    />
                </Box>
            </Popover>
        </React.Fragment>
    );
}

export default TimeSlotSelectFilter;
